import { useNavigate } from "react-router-dom";
import { useAuth, API } from "@/App";
import { Button } from "@/components/ui/button";
import MainNavigation from "@/components/MainNavigation";
import { ArrowRight, Music, Star, Users, Play, Headphones, Zap, MessageSquare } from "lucide-react";
import { useEffect, useState } from "react";
import axios from "axios";

// Custom styles for the retro aesthetic
const styles = {
  heroTitle: {
    fontFamily: "'Playfair Display', serif",
    background: "linear-gradient(180deg, #fff 0%, #a855f7 100%)",
    WebkitBackgroundClip: "text",
    WebkitTextFillColor: "transparent",
    backgroundClip: "text",
  },
  highlight: {
    color: "#ec4899",
    WebkitTextFillColor: "#ec4899",
    textShadow: "0 0 20px rgba(236, 72, 153, 0.5)",
    fontStyle: "italic",
  },
  sectionTitle: {
    fontFamily: "'Playfair Display', serif",
  },
};

const LandingPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [isVisible, setIsVisible] = useState(false);
  const [featuredReviewers, setFeaturedReviewers] = useState([]);
  const [welcomeReviewers, setWelcomeReviewers] = useState([]);
  const [featuredPage, setFeaturedPage] = useState(0);
  const [welcomePage, setWelcomePage] = useState(0);

  // Animation effect
  useEffect(() => {
    const timer = setTimeout(() => setIsVisible(true), 50);
    return () => clearTimeout(timer);
  }, []);

  // Fetch featured reviewers
  useEffect(() => {
    const fetchFeatured = async () => {
      try {
        const { data } = await axios.get(`${API}/featured-reviewers`);
        setFeaturedReviewers(data.featured || []);
        setWelcomeReviewers(data.welcome || []);
      } catch (err) {
        console.error("Failed to fetch featured reviewers:", err);
      }
    };
    fetchFeatured();
  }, []);

  // Auto-redirect logged in users to their dashboard
  useEffect(() => {
    if (user) {
      if (user.role === "admin") {
        navigate("/admin", { replace: true });
      } else if (user.role === "artist") {
        navigate("/artist/dashboard", { replace: true });
      } else if (user.role === "reviewer") {
        navigate("/reviewer/dashboard", { replace: true });
      }
    }
  }, [user, navigate]);

  const handleGetStarted = () => {
    if (user) {
      if (user.role === "admin") {
        navigate("/admin");
      } else if (user.role === "artist") {
        navigate("/artist/dashboard");
      } else {
        navigate("/reviewer/dashboard");
      }
    } else {
      navigate("/auth");
    }
  };

  const handleBrowseReviewers = () => {
    navigate("/live");
  };

  const handleBecomeArtist = () => {
    if (user) {
      if (user.role === "artist") {
        navigate("/submit");
      } else {
        navigate("/artist/dashboard");
      }
    } else {
      // Navigate to auth with artist pre-selected
      navigate("/auth?role=artist");
    }
  };

  const handleBecomeReviewer = () => {
    if (user) {
      navigate("/reviewer/dashboard");
    } else {
      // Navigate to auth with reviewer pre-selected
      navigate("/auth?role=reviewer");
    }
  };

  const handleSubmitMusic = () => {
    if (user) {
      navigate("/submit");
    } else {
      navigate("/auth");
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#1a0b2e] via-[#2d1b4e] to-[#4a2c6d] text-white overflow-x-hidden">
      {/* Retro Grid Background */}
      <div 
        className="fixed inset-0 pointer-events-none z-0"
        style={{
          backgroundImage: `
            linear-gradient(rgba(124, 58, 237, 0.1) 1px, transparent 1px),
            linear-gradient(90deg, rgba(124, 58, 237, 0.1) 1px, transparent 1px)
          `,
          backgroundSize: '50px 50px',
        }}
      />
      
      {/* Scanline Effect */}
      <div 
        className="fixed inset-0 pointer-events-none z-[100] opacity-30"
        style={{
          background: `repeating-linear-gradient(
            0deg,
            rgba(0, 0, 0, 0.15),
            rgba(0, 0, 0, 0.15) 1px,
            transparent 1px,
            transparent 2px
          )`,
          animation: 'scanline 8s linear infinite',
        }}
      />

      <MainNavigation />

      <div className="relative z-10 max-w-[1400px] mx-auto px-4 sm:px-8">
        {/* Hero Section */}
        <section className={`pt-32 sm:pt-40 pb-16 sm:pb-24 text-center transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          {/* Badge */}
          <div className="inline-block bg-pink-500/20 border-2 border-pink-500 rounded-full px-6 py-2 mb-8 animate-pulse">
            <span className="text-xs sm:text-sm font-bold tracking-widest uppercase">
              ★ THE FUTURE OF MUSIC FEEDBACK ★
            </span>
          </div>

          {/* Main Title */}
          <h1 
            className="text-5xl sm:text-6xl lg:text-[6.5rem] font-black leading-none mb-6 tracking-tight"
            style={styles.heroTitle}
          >
            Get Your Music<br />
            <span style={styles.highlight}>Reviewed by Experts</span>
          </h1>

          {/* Subtitle */}
          <p className="text-lg sm:text-xl text-white/70 max-w-2xl mx-auto mb-10 leading-relaxed font-light">
            Connect with industry reviewers, get professional feedback, and boost your music career. 
            Join the platform where independent artists meet influential voices.
          </p>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              onClick={handleBecomeArtist}
              className="bg-gradient-to-r from-pink-500 to-purple-500 hover:from-pink-600 hover:to-purple-600 text-white px-8 py-6 rounded-full font-bold text-lg shadow-[0_8px_20px_rgba(236,72,153,0.4)] hover:shadow-[0_12px_30px_rgba(236,72,153,0.6)] hover:-translate-y-1 transition-all duration-300"
              data-testid="hero-become-artist-btn"
            >
              Become an Artist <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
            <Button
              onClick={handleBecomeReviewer}
              variant="outline"
              className="bg-transparent border-2 border-purple-500 text-white px-8 py-6 rounded-full font-bold text-lg hover:bg-purple-500 hover:-translate-y-1 transition-all duration-300 shadow-[0_8px_20px_rgba(124,58,237,0.3)]"
              data-testid="hero-become-reviewer-btn"
            >
              Become a Reviewer
            </Button>
          </div>
        </section>

        {/* Stats Section */}
        <section className={`grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 mb-20 transition-all duration-1000 delay-300 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          {[
            { icon: "🎵", value: "10K+", label: "Active Artists" },
            { icon: "👥", value: "500+", label: "Expert Reviewers" },
            { icon: "▶", value: "50K+", label: "Tracks Reviewed" },
            { icon: "⭐", value: "25K+", label: "5-Star Reviews" },
          ].map((stat, idx) => (
            <div 
              key={idx}
              className="bg-[#4a2c6d]/30 border-2 border-purple-500 rounded-3xl p-6 sm:p-8 text-center backdrop-blur-xl hover:-translate-y-2 transition-all duration-300 group relative overflow-hidden"
            >
              <div className="absolute inset-0 bg-gradient-to-br from-pink-500/10 to-purple-500/10 opacity-0 group-hover:opacity-100 transition-opacity" />
              <span className="text-3xl sm:text-4xl block mb-3">{stat.icon}</span>
              <div className="text-3xl sm:text-5xl font-black text-pink-500 mb-2" style={{ fontFamily: "'Archivo', sans-serif" }}>
                {stat.value}
              </div>
              <div className="text-xs sm:text-sm uppercase tracking-wider text-white/60 font-semibold">
                {stat.label}
              </div>
            </div>
          ))}
        </section>

        {/* Welcome Reviewer Spotlight */}
        {welcomeReviewers.length > 0 && (
          <section className="py-16 sm:py-20 text-center">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black mb-4 relative inline-block" style={styles.sectionTitle}>
              Welcome
              <div className="absolute -bottom-3 left-1/2 -translate-x-1/2 w-20 h-1 bg-gradient-to-r from-pink-500 to-purple-500 rounded-full" />
            </h2>
            <p className="text-white/60 mb-12 text-lg">
              {welcomeReviewers.length === 1 ? "Our newest addition to the Afterplay community" : "Our newest additions to the Afterplay community"}
            </p>
            
            {welcomeReviewers.length <= 3 ? (
              <div className={`grid ${welcomeReviewers.length === 1 ? 'grid-cols-1 max-w-lg mx-auto' : welcomeReviewers.length === 2 ? 'grid-cols-2 max-w-2xl mx-auto' : 'grid-cols-1 sm:grid-cols-3'} gap-4 sm:gap-6`}>
                {welcomeReviewers.map((reviewer, idx) => (
                  <div
                    key={reviewer.id || idx}
                    className="bg-gradient-to-br from-[#4a2c6d]/40 to-[#2d1b4e]/60 border-2 border-pink-500/60 rounded-3xl p-8 sm:p-10 text-center backdrop-blur-xl hover:border-pink-400 hover:-translate-y-2 transition-all duration-400 cursor-pointer"
                    onClick={() => navigate(`/${reviewer.profile_code || reviewer.username || reviewer.id}`)}
                    data-testid={`welcome-reviewer-card-${idx}`}
                  >
                    {reviewer.profile_photo ? (
                      <img src={reviewer.profile_photo} alt={reviewer.name} className="w-24 h-24 mx-auto mb-5 rounded-full object-cover border-3 border-pink-500 shadow-[0_10px_30px_rgba(236,72,153,0.4)]" />
                    ) : (
                      <div className="w-24 h-24 mx-auto mb-5 bg-gradient-to-br from-purple-500 to-pink-500 rounded-full flex items-center justify-center text-3xl font-black border-3 border-pink-500 shadow-[0_10px_30px_rgba(236,72,153,0.4)]">
                        {(reviewer.name || "?").charAt(0).toUpperCase()}
                      </div>
                    )}
                    <div className="text-xl font-bold tracking-tight mb-1" style={{ fontFamily: "'Archivo', sans-serif" }}>{reviewer.name}</div>
                    <div className="text-pink-500 text-sm uppercase tracking-wider font-semibold mb-3">{reviewer.genre}</div>
                    {reviewer.bio && <p className="text-white/60 text-sm mb-5 leading-relaxed max-w-sm mx-auto">{reviewer.bio}</p>}
                    <div className="flex justify-center gap-8 text-sm">
                      <div className="flex flex-col">
                        <span className="text-purple-400 font-bold text-xl">{reviewer.reviews}</span>
                        <span className="text-white/50 text-xs">Reviews</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              /* Carousel for 4+ items */
              <div className="relative">
                <div className="overflow-hidden">
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6">
                    {welcomeReviewers.slice(welcomePage * 3, welcomePage * 3 + 3).map((reviewer, idx) => (
                      <div
                        key={reviewer.id || idx}
                        className="bg-gradient-to-br from-[#4a2c6d]/40 to-[#2d1b4e]/60 border-2 border-pink-500/60 rounded-3xl p-8 text-center backdrop-blur-xl hover:border-pink-400 hover:-translate-y-2 transition-all duration-400 cursor-pointer"
                        onClick={() => navigate(`/${reviewer.profile_code || reviewer.username || reviewer.id}`)}
                      >
                        {reviewer.profile_photo ? (
                          <img src={reviewer.profile_photo} alt={reviewer.name} className="w-20 h-20 mx-auto mb-4 rounded-full object-cover border-3 border-pink-500 shadow-[0_10px_30px_rgba(236,72,153,0.4)]" />
                        ) : (
                          <div className="w-20 h-20 mx-auto mb-4 bg-gradient-to-br from-purple-500 to-pink-500 rounded-full flex items-center justify-center text-2xl font-black border-3 border-pink-500">{(reviewer.name || "?").charAt(0).toUpperCase()}</div>
                        )}
                        <div className="text-lg font-bold tracking-tight mb-1" style={{ fontFamily: "'Archivo', sans-serif" }}>{reviewer.name}</div>
                        <div className="text-pink-500 text-xs uppercase tracking-wider font-semibold mb-3">{reviewer.genre}</div>
                        <span className="text-purple-400 font-bold">{reviewer.reviews}</span>
                        <span className="text-white/50 text-xs ml-1">Reviews</span>
                      </div>
                    ))}
                  </div>
                </div>
                {Math.ceil(welcomeReviewers.length / 3) > 1 && (
                  <div className="flex justify-center gap-2 mt-6">
                    {Array.from({ length: Math.ceil(welcomeReviewers.length / 3) }).map((_, i) => (
                      <button key={i} onClick={() => setWelcomePage(i)} className={`w-2.5 h-2.5 rounded-full transition-all ${welcomePage === i ? 'bg-pink-500 w-8' : 'bg-white/20 hover:bg-white/40'}`} />
                    ))}
                  </div>
                )}
              </div>
            )}
          </section>
        )}

        {/* Featured Reviewers Section */}
        {featuredReviewers.length > 0 && (
        <section className="py-16 sm:py-24 text-center">
          <h2 className="text-4xl sm:text-5xl lg:text-6xl font-black mb-4 relative inline-block" style={styles.sectionTitle}>
            Featured Reviewers
            <div className="absolute -bottom-3 left-1/2 -translate-x-1/2 w-24 h-1 bg-gradient-to-r from-pink-500 to-purple-500 rounded-full" />
          </h2>
          <p className="text-white/60 mb-16 text-lg">
            Connect with our top-rated music industry professionals
          </p>

          {featuredReviewers.length <= 3 ? (
            <div className={`grid ${featuredReviewers.length === 1 ? 'grid-cols-1 max-w-sm mx-auto' : featuredReviewers.length === 2 ? 'grid-cols-2 max-w-2xl mx-auto' : 'grid-cols-1 sm:grid-cols-3'} gap-4 sm:gap-6`}>
              {featuredReviewers.map((reviewer, idx) => (
                <div 
                  key={reviewer.id || idx}
                  className="bg-[#4a2c6d]/30 border-2 border-purple-500 rounded-3xl p-6 sm:p-8 text-center backdrop-blur-xl hover:border-pink-500 hover:-translate-y-3 hover:scale-105 transition-all duration-400 group cursor-pointer"
                  onClick={() => navigate(`/${reviewer.profile_code || reviewer.username || reviewer.id}`)}
                  data-testid={`featured-reviewer-${idx}`}
                >
                  {reviewer.profile_photo ? (
                    <img src={reviewer.profile_photo} alt={reviewer.name} className="w-20 h-20 sm:w-24 sm:h-24 mx-auto mb-4 rounded-full object-cover border-3 border-pink-500 shadow-[0_10px_30px_rgba(236,72,153,0.4)]" />
                  ) : (
                    <div className="w-20 h-20 sm:w-24 sm:h-24 mx-auto mb-4 bg-gradient-to-br from-purple-500 to-pink-500 rounded-full flex items-center justify-center text-2xl sm:text-3xl font-black border-3 border-pink-500 shadow-[0_10px_30px_rgba(236,72,153,0.4)]">
                      {(reviewer.name || "?").charAt(0).toUpperCase()}
                    </div>
                  )}
                  <div className="text-lg sm:text-xl font-bold tracking-tight mb-1" style={{ fontFamily: "'Archivo', sans-serif" }}>
                    {reviewer.name}
                  </div>
                  <div className="text-pink-500 text-xs sm:text-sm uppercase tracking-wider font-semibold mb-4">
                    {reviewer.genre}
                  </div>
                  <div className="flex justify-center gap-6 text-sm">
                    <div className="flex flex-col">
                      <span className="text-purple-400 font-bold text-lg">{reviewer.reviews}</span>
                      <span className="text-white/50 text-xs">Reviews</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            /* Carousel for 4+ featured */
            <div className="relative">
              <div className="overflow-hidden">
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6">
                  {featuredReviewers.slice(featuredPage * 3, featuredPage * 3 + 3).map((reviewer, idx) => (
                    <div 
                      key={reviewer.id || idx}
                      className="bg-[#4a2c6d]/30 border-2 border-purple-500 rounded-3xl p-6 sm:p-8 text-center backdrop-blur-xl hover:border-pink-500 hover:-translate-y-3 hover:scale-105 transition-all duration-400 group cursor-pointer"
                      onClick={() => navigate(`/${reviewer.profile_code || reviewer.username || reviewer.id}`)}
                    >
                      {reviewer.profile_photo ? (
                        <img src={reviewer.profile_photo} alt={reviewer.name} className="w-20 h-20 sm:w-24 sm:h-24 mx-auto mb-4 rounded-full object-cover border-3 border-pink-500 shadow-[0_10px_30px_rgba(236,72,153,0.4)]" />
                      ) : (
                        <div className="w-20 h-20 sm:w-24 sm:h-24 mx-auto mb-4 bg-gradient-to-br from-purple-500 to-pink-500 rounded-full flex items-center justify-center text-2xl sm:text-3xl font-black border-3 border-pink-500">{(reviewer.name || "?").charAt(0).toUpperCase()}</div>
                      )}
                      <div className="text-lg sm:text-xl font-bold tracking-tight mb-1" style={{ fontFamily: "'Archivo', sans-serif" }}>{reviewer.name}</div>
                      <div className="text-pink-500 text-xs sm:text-sm uppercase tracking-wider font-semibold mb-4">{reviewer.genre}</div>
                      <span className="text-purple-400 font-bold text-lg">{reviewer.reviews}</span>
                      <span className="text-white/50 text-xs ml-1">Reviews</span>
                    </div>
                  ))}
                </div>
              </div>
              {Math.ceil(featuredReviewers.length / 3) > 1 && (
                <div className="flex justify-center gap-2 mt-6">
                  {Array.from({ length: Math.ceil(featuredReviewers.length / 3) }).map((_, i) => (
                    <button key={i} onClick={() => setFeaturedPage(i)} className={`w-2.5 h-2.5 rounded-full transition-all ${featuredPage === i ? 'bg-purple-500 w-8' : 'bg-white/20 hover:bg-white/40'}`} />
                  ))}
                </div>
              )}
            </div>
          )}
        </section>
        )}

        {/* How It Works Section */}
        <section className="py-16 sm:py-24 text-center">
          <h2 className="text-4xl sm:text-5xl lg:text-6xl font-black mb-4 relative inline-block" style={styles.sectionTitle}>
            How It Works
            <div className="absolute -bottom-3 left-1/2 -translate-x-1/2 w-24 h-1 bg-gradient-to-r from-pink-500 to-purple-500 rounded-full" />
          </h2>
          <p className="text-white/60 mb-16 text-lg">
            A streamlined process to get your music heard by the right people
          </p>

          <div className="grid md:grid-cols-3 gap-8 sm:gap-10">
            {[
              {
                step: "01",
                icon: "🎧",
                title: "Submit Your Track",
                description: "Upload your music and add details, and choose your preferred reviewer. Select from live or offline review options."
              },
              {
                step: "02",
                icon: "⚡",
                title: "Boost Priority",
                description: "Move up in the queue by boosting your submission. Subscribers can auto-boost your track to get faster feedback."
              },
              {
                step: "03",
                icon: "💬",
                title: "Get Reviewed",
                description: "Receive detailed feedback from industry experts. Live reviews happen on YouTube, TikTok, or Instagram with instant engagement."
              }
            ].map((step, idx) => (
              <div 
                key={idx}
                className="relative bg-[#4a2c6d]/20 border-2 border-[#4a2c6d] rounded-3xl p-8 pt-12 hover:border-pink-500 hover:-translate-y-3 transition-all duration-400 group backdrop-blur-xl"
              >
                {/* Step Number */}
                <div className="absolute -top-5 left-1/2 -translate-x-1/2 w-14 h-14 bg-gradient-to-br from-pink-500 to-purple-500 rounded-full flex items-center justify-center text-xl font-black shadow-[0_10px_30px_rgba(236,72,153,0.4)]">
                  {step.step}
                </div>
                <span className="text-5xl block mb-6">{step.icon}</span>
                <h3 className="text-xl sm:text-2xl font-bold mb-4 tracking-tight" style={{ fontFamily: "'Archivo', sans-serif" }}>
                  {step.title}
                </h3>
                <p className="text-white/60 leading-relaxed">
                  {step.description}
                </p>
              </div>
            ))}
          </div>
        </section>

        {/* For Artists & Reviewers Section */}
        <section className="py-16 sm:py-24 grid lg:grid-cols-2 gap-8 sm:gap-10">
          {/* For Artists */}
          <div className="bg-[#4a2c6d]/20 border-3 border-purple-500 rounded-[2rem] p-8 sm:p-10 relative overflow-hidden hover:border-pink-500 hover:-translate-y-2 transition-all duration-400 backdrop-blur-xl">
            <div className="absolute inset-0 bg-gradient-radial from-pink-500/10 to-transparent pointer-events-none" />
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black mb-4 relative" style={styles.sectionTitle}>
              🎸 For Artists
            </h2>
            <p className="text-white/70 text-lg mb-6">
              Get your music in front of industry professionals and build your fanbase
            </p>
            <ul className="space-y-4 mb-8">
              {[
                "Submit tracks to expert reviewers in your genre",
                "Choose between live or offline reviews",
                "Get honest, constructive feedback to improve your craft",
                "Boost visibility with priority queue options",
                "Connect directly with your audience during live reviews",
                "Build credibility with professional endorsements"
              ].map((item, idx) => (
                <li key={idx} className="text-white/70 border-b border-purple-500/30 pb-4 pl-6 relative">
                  <span className="absolute left-0 text-pink-500 text-xl">▸</span>
                  {item}
                </li>
              ))}
            </ul>
            <Button
              onClick={handleSubmitMusic}
              className="bg-gradient-to-r from-pink-500 to-purple-500 hover:from-pink-600 hover:to-purple-600 text-white px-8 py-5 rounded-full font-bold text-base shadow-[0_8px_20px_rgba(236,72,153,0.4)] hover:shadow-[0_12px_30px_rgba(236,72,153,0.6)] hover:-translate-y-1 transition-all duration-300"
            >
              Submit Your Music
            </Button>
          </div>

          {/* For Reviewers */}
          <div className="bg-[#4a2c6d]/20 border-3 border-purple-500 rounded-[2rem] p-8 sm:p-10 relative overflow-hidden hover:border-pink-500 hover:-translate-y-2 transition-all duration-400 backdrop-blur-xl">
            <div className="absolute inset-0 bg-gradient-radial from-pink-500/10 to-transparent pointer-events-none" />
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black mb-4 relative" style={styles.sectionTitle}>
              🎤 For Reviewers
            </h2>
            <p className="text-white/70 text-lg mb-6">
              Monetize your expertise and help shape the next generation of artists
            </p>
            <ul className="space-y-4 mb-8">
              {[
                "Earn money reviewing music you're passionate about",
                "Set your own rates and availability",
                "Build your personal brand and following",
                "Stream reviews live or record offline content",
                "Connect with emerging talent in your genre",
                "Flexible schedule - review on your own time"
              ].map((item, idx) => (
                <li key={idx} className="text-white/70 border-b border-purple-500/30 pb-4 pl-6 relative">
                  <span className="absolute left-0 text-pink-500 text-xl">▸</span>
                  {item}
                </li>
              ))}
            </ul>
            <Button
              onClick={handleBecomeReviewer}
              variant="outline"
              className="bg-transparent border-2 border-purple-500 text-white px-8 py-5 rounded-full font-bold text-base hover:bg-purple-500 hover:-translate-y-1 transition-all duration-300 shadow-[0_8px_20px_rgba(124,58,237,0.3)]"
            >
              Become a Reviewer
            </Button>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 sm:py-24 my-8 text-center bg-pink-500/10 border-y-2 border-pink-500 rounded-[3rem] relative overflow-hidden">
          <div 
            className="absolute inset-0 pointer-events-none"
            style={{
              background: `repeating-linear-gradient(
                45deg,
                transparent,
                transparent 10px,
                rgba(124, 58, 237, 0.05) 10px,
                rgba(124, 58, 237, 0.05) 20px
              )`
            }}
          />
          <div className="relative z-10 px-4">
            <h2 className="text-4xl sm:text-5xl lg:text-6xl font-black mb-6 leading-tight" style={styles.sectionTitle}>
              Ready to Get Your<br />Music Reviewed?
            </h2>
            <p className="text-white/70 text-lg sm:text-xl max-w-2xl mx-auto mb-10 leading-relaxed">
              Join thousands of artists who have elevated their music career through professional feedback. 
              Your next breakthrough starts here.
            </p>
            <Button
              onClick={handleGetStarted}
              className="bg-gradient-to-r from-pink-500 to-purple-500 hover:from-pink-600 hover:to-purple-600 text-white px-10 py-6 rounded-full font-bold text-lg sm:text-xl shadow-[0_8px_20px_rgba(236,72,153,0.4)] hover:shadow-[0_12px_30px_rgba(236,72,153,0.6)] hover:-translate-y-1 transition-all duration-300"
              data-testid="cta-get-started-btn"
            >
              Start Now <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </div>
        </section>

        {/* Footer */}
        <footer className="py-8 sm:py-10 text-center border-t border-purple-500/30">
          <p className="text-white/40 text-sm">
            © 2026 Afterplay. All rights reserved. | Built for independent artists worldwide.
          </p>
        </footer>
      </div>

      {/* Custom Keyframes */}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@700;900&family=Archivo:wght@400;700;900&display=swap');
        
        @keyframes scanline {
          0% { transform: translateY(0); }
          100% { transform: translateY(50px); }
        }
        
        @keyframes float {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-20px); }
        }
      `}</style>
    </div>
  );
};

export default LandingPage;
